import { useEffect, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWorkspace } from 'hooks/useWorkspace';

import HelpButton from 'Components/HelpButton';

import { getDesignReviewCountsAction,
  manageAuditAction, setDesignReviewHistory } from 'Redux/Actions/designReviewActions';
import { setCurrentRecord } from 'Redux/Actions/historyActions';
import { setUserProfile } from 'Redux/Actions/userActions';

import { ROUTES } from 'routes/route.constants';

import { PLATFORM } from 'utlis/constants';
import { errorReactToastify, successReactToastify } from 'utlis/toasts';

import DashboardHeader from './components/DashboardHeader';
import EditAuditSidebar from './components/EditAuditSidebar';
import LowCreditsAlert from './components/LowCreditsAlert';
import Overview from './components/Overview';
import ReviewsSection from './components/ReviewsSection';
import Sidebar from './components/Sidebar';

import styles from './styles.module.scss';

const DashboardHome = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locationObject = useLocation();
  const { selectedWorkspace } = useWorkspace();

  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);

  const authUser = useSelector(state => state.userStore);
  const reviewCount = useSelector(state => state.designReview.reviewCount);
  const designReviewHistory = useSelector(state => state.designReview.designReviewHistory);
  const [previousReviewsRemaining, setPreviousReviewsRemaining] = useState(null);
  const [showLowCreditsAlert, setShowLowCreditsAlert] = useState(false);

  // Edit sidebar state — lifted here so the sidebar can be rendered outside overflow-auto
  const [editingReview, setEditingReview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [restoringReviewUuid, setRestoringReviewUuid] = useState(null);

  // Increment these to signal ReviewsSection to refetch the corresponding list
  const [activeRefreshKey, setActiveRefreshKey] = useState(0);
  const [archivedRefreshKey, setArchivedRefreshKey] = useState(0);

  // Check if we're on a create or review detail route
  const reviewsBasePath = ROUTES.TEAM_REVIEWS_NEW.replace('/new', '');
  const isCreateReviewOpen = Boolean(
    locationObject.pathname === ROUTES.TEAM_REVIEWS_NEW
    || (locationObject.pathname.startsWith(reviewsBasePath) && locationObject.pathname !== ROUTES.TEAM_REVIEWS_NEW)
  );

  // Handle opening review for existing review - navigate to review URL
  const handleViewReview = review => {
    if (review && review.uuid) {
      dispatch(setCurrentRecord(review));
      navigate(ROUTES.TEAM_REVIEWS_DETAIL(review.uuid));
    }
  };

  // Handle opening new review - navigate to create URL
  const handleCreateNewReview = () => {
    navigate(ROUTES.TEAM_REVIEWS_NEW);
  };

  const checkAuthentication = async () => {
    try {
      const userObjectString = localStorage.getItem('userObject');

      if (!userObjectString) {
        // Pass current URL parameters to login route
        const loginUrl = locationObject.search ? `${ROUTES.TEAM_LOGIN}${locationObject.search}` : ROUTES.TEAM_LOGIN;

        navigate(loginUrl);

        return;
      }

      const userObject = JSON.parse(userObjectString);

      if (!userObject || !userObject.uuid) {
        // Pass current URL parameters to login route
        const loginUrl = locationObject.search ? `${ROUTES.TEAM_LOGIN}${locationObject.search}` : ROUTES.TEAM_LOGIN;

        navigate(loginUrl);

        return;
      }

      setIsCheckingAuth(false);
    }
    catch (error) {
      // Handle invalid JSON in localStorage
      // eslint-disable-next-line no-console
      console.error('Error parsing userObject from localStorage:', error);
      localStorage.removeItem('userObject');

      const loginUrl = locationObject.search ? `${ROUTES.TEAM_LOGIN}${locationObject.search}` : ROUTES.TEAM_LOGIN;

      navigate(loginUrl);
    }
  };

  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (authUser && authUser.uuid) {
      dispatch(setUserProfile(authUser));
    }
  }, [authUser?.uuid, dispatch]);

  // Check localStorage for modal state on component mount (only after auth check)
  useEffect(() => {
    // Wait for auth check to complete
    if (isCheckingAuth) {
      return;
    }

    const shouldReopenModal = localStorage.getItem('teamReview_shouldReopenModal') === 'true';

    if (shouldReopenModal) {
      // Refresh user profile to get updated Figma token after OAuth
      if (authUser && authUser.uuid) {
        dispatch(setUserProfile(authUser));
      }

      handleCreateNewReview();

      // Clear the localStorage flags after navigating
      localStorage.removeItem('teamReview_shouldReopenModal');
    }
  }, [isCheckingAuth, authUser, dispatch]);

  // Check if user has no credits and show alert
  useEffect(() => {
    if (reviewCount && reviewCount.reviews_remaining !== undefined) {
      const currentRemaining = reviewCount.reviews_remaining;

      // Show alert if user has 0 credits, either:
      // 1. Just ran out (had credits before, now has 0)
      // 2. Already has 0 credits when loading the page (first time check)
      if (currentRemaining === 0) {
        if (
          // Case 1: Just ran out of credits
          (previousReviewsRemaining !== null && previousReviewsRemaining > 0)
          // Case 2: First time loading and already has 0 credits
          || (previousReviewsRemaining === null)
        ) {
          setShowLowCreditsAlert(true);
        }
      }

      // Update previous value for next comparison
      setPreviousReviewsRemaining(currentRemaining);
    }
  }, [reviewCount?.reviews_remaining, previousReviewsRemaining]);

  const handleShowLowCreditsAlert = () => {
    setShowLowCreditsAlert(true);
  };

  const handleCloseLowCreditsAlert = () => {
    setShowLowCreditsAlert(false);
  };

  // --- Edit sidebar handlers ---

  const handleEditReview = review => {
    setEditingReview(review);
  };

  const handleCloseSidebar = () => {
    setEditingReview(null);
  };

  const handleSaveTitle = async newTitle => {
    setIsSaving(true);

    try {
      await dispatch(manageAuditAction(editingReview.uuid, { title: newTitle }));

      // Immediately update the review title in Redux so the list reflects the change
      // without waiting for a refetch. review_frame_name is the field shown in the list.
      const updatedResults = designReviewHistory.results?.map(r => r.uuid === editingReview.uuid
        ? { ...r, extra_data: { ...r.extra_data, review_frame_name: newTitle } }
        : r
      );

      dispatch(setDesignReviewHistory({ ...designReviewHistory, results: updatedResults }));

      successReactToastify('Flow details successfully updated', { autoClose: 3000 });
      handleCloseSidebar();
    }
    catch {
      errorReactToastify('Failed to update flow title. Please try again.', { autoClose: 3000 });
    }
    finally {
      setIsSaving(false);
    }
  };

  const handleArchiveReview = async () => {
    setIsArchiving(true);

    const reviewTitle = editingReview.extra_data?.review_frame_name || 'Flow';

    try {
      await dispatch(manageAuditAction(editingReview.uuid, { is_archived: true }));
      successReactToastify(`${reviewTitle} has been archived`, { autoClose: 3000 });
      handleCloseSidebar();
      setActiveRefreshKey(k => k + 1);
      setArchivedRefreshKey(k => k + 1);
      dispatch(getDesignReviewCountsAction(PLATFORM, selectedWorkspace?.uuid));
    }
    catch {
      errorReactToastify('Failed to archive flow. Please try again.', { autoClose: 3000 });
    }
    finally {
      setIsArchiving(false);
    }
  };

  const handleRestoreReview = async review => {
    const reviewTitle = review.extra_data?.review_frame_name || 'Flow';

    setRestoringReviewUuid(review.uuid);

    try {
      await dispatch(manageAuditAction(review.uuid, { is_archived: false }));
      successReactToastify(`${reviewTitle} has been restored`, { autoClose: 3000 });
      setArchivedRefreshKey(k => k + 1);
      setActiveRefreshKey(k => k + 1);
      dispatch(getDesignReviewCountsAction(PLATFORM, selectedWorkspace?.uuid));
    }
    catch {
      errorReactToastify('Failed to restore flow. Please try again.', { autoClose: 3000 });
    }
    finally {
      setRestoringReviewUuid(null);
    }
  };

  // Show loader while checking authentication
  if (isCheckingAuth) {
    return (
      <div className={styles['loader-container']}>
        <ImpulseSpinner frontColor={'#28BDAE'} />
      </div>
    );
  }

  return (
    <div className={styles['dashboard-container']}>
      <div className="min-h-screen bg-gray-50 grid gap-0 flex w-full">
        <Sidebar />
        <div className="flex flex-1 bg-gray-50 p-2 pl-0 max-h-screen">
          {/*
           * This div is `position: relative` — the EditAuditSidebar renders
           * as an absolute child of this element, covering it fully.
           * It must NOT be inside the overflow-auto inner div below.
           */}
          <div className="flex flex-1 flex-col overflow-auto rounded-lg bg-white shadow-figma-sm">
            <DashboardHeader onShowLowCreditsAlert={handleShowLowCreditsAlert} />

            <div className="flex flex-1 overflow-auto flex-col px-6 pt-6 pb-12">
              <Overview
                onStatusFilter={setStatusFilter}
                statusFilter={statusFilter}
              />

              <ReviewsSection
                isCreateReviewOpen={isCreateReviewOpen}
                statusFilter={statusFilter}
                onClearStatusFilter={() => setStatusFilter(null)}
                onViewReview={handleViewReview}
                onCreateReviewToggle={handleCreateNewReview}
                onEditReview={handleEditReview}
                onRestoreReview={handleRestoreReview}
                restoringReviewUuid={restoringReviewUuid}
                activeRefreshKey={activeRefreshKey}
                archivedRefreshKey={archivedRefreshKey}
              />
            </div>

            <HelpButton />

            {/*
             * EditAuditSidebar is rendered here — direct child of the `relative` white card,
             * NOT inside the overflow-auto div — so its absolute positioning covers the full card.
             */}
            {editingReview && (
              <EditAuditSidebar
                review={editingReview}
                onClose={handleCloseSidebar}
                onArchive={handleArchiveReview}
                onSave={handleSaveTitle}
                isSaving={isSaving}
                isArchiving={isArchiving}
              />
            )}
          </div>
        </div>
      </div>

      {/* Low Credits Alert */}
      {showLowCreditsAlert && (
        <LowCreditsAlert onClose={handleCloseLowCreditsAlert} />
      )}
    </div>
  );
};

export default DashboardHome;
